import * as angular from 'angular';
import {
    CompatibilityService as CompatibilityService_Base, IDeferred,
    IRequestShortcutConfig, IHttpResponse, IQService
} from '@common/services/compatibilityservice';
import { IWebApiRoot } from '@common/navdata';
import { InjectionResolve, INavNgInjectable } from '@services/autoresolveservice';
import { IAutoResolvable } from '@common/services/autoresolveservice';
import { fn } from '@common/utils';
import { InfomaOnlineGuardAuth } from '@common/rwf/rwf.infomaonline';
export { IDeferred, IRequestShortcutConfig, IHttpResponse, IQService }

/** Service Implementation "hiding" angular(js) Implementation Details for preparing migration process */
export class CompatibilityService extends CompatibilityService_Base {
    static $inject = ["WebApiRoot", "$q", "$http", "$parse", "$timeout", "$rootScope", "$cacheFactory", "$injector"];
    constructor(
        webApiRoot: IWebApiRoot,
        private readonly $q: ng.IQService,
        private readonly $http: ng.IHttpService,
        private readonly $parse: ng.IParseService,
        private readonly $timeout: ng.ITimeoutService,
        private readonly $rootScope: ng.IRootScopeService,
        private readonly $chacheFactory: ng.ICacheFactoryService,
        $injector: ng.auto.IInjectorService
    ) {
        super(webApiRoot);
        this.autoResolve = InjectionResolve.bind(null, $injector);
    }
    private readonly autoResolve: IAutoResolvable<INavNgInjectable>;

    public reject<T = never>(reason: any): PromiseLike<T> {
        return this.$q.reject(reason);
    }

    public when(): PromiseLike<void>
    public when<T>(t: T | PromiseLike<T>): PromiseLike<T>;
    public when<T>(t?: T | PromiseLike<T>) {
        return this.$q.when(t);
    }

    public defer<T = any>() {
        return <IDeferred<T>>this.$q.defer<T>();
    }

    public finally<T = any, TPromise extends PromiseLike<T> = PromiseLike<T>>(p: TPromise, action: Function) {
        let result: any;
        return <TPromise>p.then((s) => {
            result = s;
            if (action) {
                return action(s);
            }
            return result;
        }, err => {
            if (action) {
                var s = action(err);
            }
            if (fn.isPromise(s))
                return s.then(() => {
                    throw err;
                }, _err => {
                    throw err;
                });
            throw err;
        }).then(() => result);
    }

    public all<TAll>(promises: Array<TAll | PromiseLike<TAll>>): PromiseLike<TAll[]>;
    public all<T>(promises: { [K in keyof T]: (PromiseLike<T[K]> | T[K]); }): PromiseLike<T>;
    public all<TAll>(promises: Array<TAll | PromiseLike<TAll>>) {
        return this.$q.all(<any>promises);
    }

    public get<T>(url: string, config?: IRequestShortcutConfig): PromiseLike<IHttpResponse<T>> {
        url = super.url(url);
        return this.guardedBackendAction(() => this.$http.get<T>(url, this.adjustConfig(config, url)));
    }

    public post<T>(url: string, data: any, config?: IRequestShortcutConfig): PromiseLike<IHttpResponse<T>> {
        url = super.url(url);
        return this.guardedBackendAction(() => this.$http.post<T>(url, data, this.adjustConfig(config, url)));
    }

    private guardedBackendAction<T>(guardedAction: () => PromiseLike<angular.IHttpResponse<T>>) {
      // TODO refactor the dependency
      return InfomaOnlineGuardAuth(this, guardedAction);
    }

    clearCache() {
        const $cacheFactory = this.$chacheFactory;
        const httpCache = $cacheFactory && $cacheFactory.get("$http");
        if (httpCache) {
            httpCache.removeAll();
        }
    }

    public get parse() {
        return this.$parse;
    }

    public get timeout() {
        return this.$timeout;
    }

    public get resolve() {
        return this.autoResolve;
    }

    public tick(element?: Document | Node | JQuery) {
        let $scope: ng.IScope;
        if (element) {
            $scope = angular.element(element).scope();
        } else {
            $scope = this.$rootScope;
        }

        if ($scope && !$scope.$root.$$phase) {
            $scope.$root.$apply();
        }
    }

    /** "external webApi: provide credentials" */
    private adjustConfig(config: ng.IRequestShortcutConfig, url: string) {
        const webApiRoot = this.webApiRoot;
        if (webApiRoot && webApiRoot.WithCredentials) {
            config = config || {};
            config.withCredentials = true;
        }
        return config;
    }
}

declare global {

    interface PromiseLike<T> {

        then<TResult1 = T, TResult2 = never>(onfulfilled?: ((value: T) => TResult1 | ng.IPromise<TResult1>) | undefined | null, onrejected?: ((reason: any) => TResult2 | ng.IPromise<TResult2>) | undefined | null): PromiseLike<TResult1 | TResult2>;
        //
        //then<T2>(succ: (s: T) => ng.IPromise<T2>): PromiseLike<T2>;
    }
}


